import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { cleared, onPollError, onProgress, onTick, pollDelay, startRun, type Run } from './approving';

/**
 * fix-79: the approvals still running on the desk, each followed by polling GET /progress until it ends
 * (lib/approving.ts decides every transition). `onEnded` runs when one ends, so the queue and the counts refresh.
 */
export function useRuns(onEnded: () => void) {
  const [runs, setRuns] = useState<Run[]>([]);
  const current = useRef<Run[]>([]);
  current.current = runs;
  const due = useRef(new Map<number, number>());
  const asking = useRef(new Set<number>());
  const ended = useRef(onEnded);
  ended.current = onEnded;

  const update = useCallback((id: number, fn: (r: Run) => Run) => {
    setRuns((rs) => rs.map((r) => {
      if (r.id !== id) return r;
      const next = fn(r);
      if (r.phase === 'running' && next.phase !== 'running') setTimeout(() => ended.current(), 0);
      return next;
    }));
  }, []);

  const start = useCallback((card: { id: number; type: string }, who: string) => {
    due.current.set(card.id, Date.now() + 500);
    setRuns((rs) => [...rs.filter((r) => r.id !== card.id), startRun(card, who, Date.now())]);
  }, []);

  const close = useCallback((id: number) => setRuns((rs) => rs.filter((r) => r.id !== id)), []);

  const active = runs.length > 0;
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => {
      const now = Date.now();
      setRuns((rs) => {
        const next = rs.map((r) => onTick(r, now));
        if (next.some((r, i) => rs[i].phase === 'running' && r.phase !== 'running')) setTimeout(() => ended.current(), 0);
        return next.filter((r) => !cleared(r, now));
      });
      for (const r of current.current) {
        if (r.phase !== 'running' || asking.current.has(r.id) || (due.current.get(r.id) ?? 0) > now) continue;
        asking.current.add(r.id);
        api.progress(r.id)
          .then((p) => update(r.id, (x) => onProgress(x, p, Date.now())))
          .catch((e: Error) => update(r.id, (x) => onPollError(x, e.message, Date.now())))
          .finally(() => {
            asking.current.delete(r.id);
            const x = current.current.find((y) => y.id === r.id);
            due.current.set(r.id, Date.now() + (x ? pollDelay(x, Date.now()) ?? 0 : 0));
          });
      }
    }, 250);
    return () => clearInterval(t);
  }, [active, update]);

  return { runs, start, close };
}
